import type { FetchData } from '@hooks/useDataFetcher';
import { useNavigation } from '@hooks/useNavigation';
import { GLOBAL_STORE } from '@shared/constants';
import { useCallback, useEffect, useState } from 'react';
import type { IStore } from 'types/custom/offers';

interface IState {
  enabled: boolean;
  stores: IStore[];
  loading: boolean;
  selectedStore?: IStore;
}

export type UseStores = IState & {
  changeStore: (market: string) => void;
  enableStores: (market?: string) => void;
  disableStores: (market?: string) => void;
};

const resolveStore = (market: string, stores: IStore[]): IStore | undefined =>
  market === GLOBAL_STORE.market.code
    ? GLOBAL_STORE
    : stores.find((s) => s.market.code === market);

export const useStores = (fetchData: FetchData): UseStores => {
  const {
    push,
    currentPath,
    params: { market = '', marketId = '' },
  } = useNavigation();
  const [state, setState] = useState<IState>({
    stores: [],
    enabled: false,
    loading: false,
    selectedStore: GLOBAL_STORE,
  });

  const urlMarket = market || marketId;

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional one-time fetch on mount
  useEffect(() => {
    if (!state.loading && !state.stores.length) {
      getStores();
    }
  }, []);

  // Sync selectedStore when enabled and stores/URL change
  useEffect(() => {
    if (!state.enabled || !urlMarket) return;
    if (state.selectedStore?.market.code === urlMarket) return;

    const store = resolveStore(urlMarket, state.stores);
    if (store) {
      setState((prev) => ({ ...prev, selectedStore: store }));
    }
  }, [
    state.enabled,
    state.stores,
    urlMarket,
    state.selectedStore?.market.code,
  ]);

  const getStores = useCallback(async (): Promise<IStore[]> => {
    setState((prev) => ({
      ...prev,
      loading: prev.stores.length > 0 ? prev.loading : true,
    }));

    try {
      const stores = await fetchData<IStore[]>('/api/offers/v1/admin/stores');
      const validStores = stores?.length ? stores : [];

      setState((prev) => ({
        ...prev,
        loading: false,
        stores: validStores,
      }));

      return validStores;
    } catch (error) {
      setState((prev) => ({ ...prev, loading: false }));
      return [];
    }
  }, [fetchData]);

  const changeStore = useCallback<UseStores['changeStore']>(
    (m) => {
      if (urlMarket) {
        push(currentPath.replace(`/${urlMarket}`, `/${m}`));
      }
    },
    [currentPath, urlMarket, push],
  );

  const enableStores = useCallback<UseStores['enableStores']>(
    (m = urlMarket) =>
      setState((prev) => ({
        ...prev,
        enabled: true,
        selectedStore: resolveStore(m, prev.stores) ?? prev.selectedStore,
      })),
    [urlMarket],
  );

  const disableStores = useCallback<UseStores['disableStores']>(
    (m = GLOBAL_STORE.market.code) =>
      setState((prev) => ({
        ...prev,
        enabled: false,
        selectedStore: resolveStore(m, prev.stores) ?? GLOBAL_STORE,
      })),
    [],
  );

  return { ...state, changeStore, enableStores, disableStores };
};
